import { M_PER_DEG_LAT, M_PER_DEG_LNG } from './geo.js';

// Generic along-a-polyline geometry, shared by every layer that glues a marker to
// a route's curve (subway bullets, road-snapped buses). All distances are meters
// under the equirectangular approximation in geo.ts (accurate at NYC scale).
//
// The point of working in along-path DISTANCE space — rather than straight-line
// lng/lat — is that mapping a distance back onto the polyline keeps the marker on
// the track/road even mid-correction. A straight-line follow cuts chords across
// curves (the bus-through-the-river artifact).

/** Default jump (m) beyond which `stepAlongPath` teleports instead of stepping —
 * only genuinely broken jumps (shape flips, GPS reacquire) should exceed it. This
 * is the subway-bullet default; the bus snapper manages its own snap guard inline. */
export const DEFAULT_SNAP_METERS = 2000;

/** Cumulative along-path distance (m) at each coord; `cum[0] === 0`. */
export function pathCumulative(path: [number, number][]): number[] {
  if (path.length === 0) return [];
  const cum = new Array<number>(path.length);
  cum[0] = 0;
  for (let i = 1; i < path.length; i++) {
    const dx = (path[i]![0] - path[i - 1]![0]) * M_PER_DEG_LNG;
    const dy = (path[i]![1] - path[i - 1]![1]) * M_PER_DEG_LAT;
    cum[i] = cum[i - 1]! + Math.hypot(dx, dy);
  }
  return cum;
}

/** Lng/lat at a given along-path distance (m); clamps to the path ends. The
 * returned point lies exactly on the polyline, so the marker stays on-track. */
export function pointAtDistance(
  path: [number, number][],
  cum: number[],
  dist: number,
): [number, number] {
  const n = Math.min(path.length, cum.length);
  if (n === 0) return [0, 0];
  if (dist <= cum[0]!) return path[0]!;
  if (dist >= cum[n - 1]!) return path[n - 1]!;
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (cum[mid]! <= dist) lo = mid;
    else hi = mid;
  }
  const d0 = cum[lo]!;
  const d1 = cum[hi]!;
  const f = d1 === d0 ? 0 : (dist - d0) / (d1 - d0);
  const a = path[lo]!;
  const b = path[hi]!;
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
}

/** Along-path distance (m) of the path point nearest to `p`. Used to migrate a
 * displayed position onto a path after a prediction/shape update, so the marker
 * keeps its on-track spot instead of jumping. */
export function projectDistance(
  path: [number, number][],
  cum: number[],
  p: [number, number],
): number {
  const px = p[0] * M_PER_DEG_LNG;
  const py = p[1] * M_PER_DEG_LAT;
  let best = 0;
  let bestErr = Infinity;
  for (let i = 1; i < path.length; i++) {
    const ax = path[i - 1]![0] * M_PER_DEG_LNG;
    const ay = path[i - 1]![1] * M_PER_DEG_LAT;
    const abx = path[i]![0] * M_PER_DEG_LNG - ax;
    const aby = path[i]![1] * M_PER_DEG_LAT - ay;
    const len2 = abx * abx + aby * aby;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * abx + (py - ay) * aby) / len2));
    const cx = ax + abx * t;
    const cy = ay + aby * t;
    const err = Math.hypot(px - cx, py - cy);
    if (err < bestErr) {
      bestErr = err;
      best = cum[i - 1]! + (cum[i]! - cum[i - 1]!) * t;
    }
  }
  return best;
}

/**
 * Advance the displayed along-path distance toward the live target, forward-only
 * and at a constant capped speed, so the marker glides along the track curve.
 *  - undefined prev (first sight) → snap to target
 *  - gap larger than snapMeters (broken jump, e.g. shape flip) → snap
 *  - backward gap (vehicles don't reverse mid-route) → hold
 *  - otherwise step at most maxStepMeters toward target
 */
export function stepAlongPath(
  prevDist: number | undefined,
  targetDist: number,
  maxStepMeters: number,
  snapMeters = DEFAULT_SNAP_METERS,
): number {
  if (prevDist === undefined) return targetDist;
  const gap = targetDist - prevDist;
  if (Math.abs(gap) > snapMeters) return targetDist;
  if (gap <= 0) return prevDist; // backward correction → hold
  if (gap <= maxStepMeters) return targetDist;
  return prevDist + maxStepMeters;
}
